import { useRef } from 'react';

import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import {
  SearchableDropdownMenu,
  type SearchableMenuGroup,
} from '@/components/ui/custom/SearchableDropdownMenu/SearchableDropdownMenu';
import type { FilterType } from '@/modules/analytics/constants/metricsCatalog';

export type DimensionOption = { column: string; label: string; type: FilterType };

type DimensionPickerProps = {
  options: DimensionOption[];
  onPick: (option: DimensionOption) => void;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
};

export const DimensionPicker = ({
  options,
  onPick,
  onOpenChange,
  disabled,
}: DimensionPickerProps) => {
  const { t } = useTranslation();
  const pickedRef = useRef(false);

  const groups: SearchableMenuGroup[] = [
    {
      id: 'dimensions',
      items: options.map((opt) => ({
        id: opt.column,
        label: opt.label,
        searchText: opt.label,
        onClick: () => {
          pickedRef.current = true;
          onPick(opt);
        },
      })),
    },
  ];

  return (
    <SearchableDropdownMenu
      trigger={
        <Button variant="outline-neutral" size="sm" type="button" disabled={disabled}>
          <PlusIcon />
          {t('analytics.reports_detail_filters')}
        </Button>
      }
      groups={groups}
      align="end"
      className="w-64"
      onOpenChange={onOpenChange}
      searchPlaceholder={t('analytics.reports_detail_filter_find_dimension')}
      emptyState={<FilterEmptyState text={t('analytics.reports_detail_filter_no_dimensions')} />}
      onCloseAutoFocus={(e) => {
        if (pickedRef.current) {
          pickedRef.current = false;
          e.preventDefault();
        }
      }}
    />
  );
};
